'use client'

import { PlaceLabel } from '@/frontend/shared/place-label'
import {
  SolveTimeLabel,
  SolveTimeLinkOrDnf,
} from '@/frontend/shared/solve-time-link-or-dnf'
import {
  ArrowRightIcon,
  DisciplineIcon,
  MinusIcon,
  PlusIcon,
  PrimaryButton,
  SecondaryButton,
  UnderlineButton,
} from '@/frontend/ui'
import { SpinningBorder } from '@/frontend/ui/spinning-border'
import { cn } from '@/frontend/utils/cn'
import { tailwindConfig } from '@/frontend/utils/tailwind'
import { getExtraNumber, type Discipline, type RoundSession } from '@/types'
import * as Accordion from '@radix-ui/react-accordion'
import Link from 'next/link'
import { type RefObject } from 'react'
import { UserBadges } from './user-badges'
import { UsernameLink } from './username-link'
import { useSolveForm } from './use-solve-form'
import { SimulatorProvider } from '@/app/(app)/contests/[contestSlug]/solve/_components'
import { LoadingDots } from '../ui/loading-dots'
import { useIsClient } from 'usehooks-ts'
import { ClassicSolveViewLink } from '@/app/(app)/contests/[contestSlug]/results/_components/classic-solve-view-link'

// HACK: we can't just use useMatchesScreen for switching between Desktop and Tablet because then it won't be SSRed properly
type RoundSessionRowProps = {
  session: RoundSession
  discipline: Discipline
  withContestLink?: boolean
  isFirstOnPage: boolean
  place: number
  className?: string
  podiumColors?: boolean
  onPlaceClick?: () => void
  revealedAttemptsNumber?: number
  isPlacePreliminary?: boolean
  sticky?: boolean
}
export function RoundSessionRow({
  discipline,
  withContestLink = false,
  isFirstOnPage,
  place,
  session,
  ref,
  className,
  podiumColors = false,
  onPlaceClick,
  revealedAttemptsNumber = 5,
  isPlacePreliminary = false,
  sticky = false,
}: RoundSessionRowProps & { ref?: RefObject<HTMLLIElement | null> }) {
  return (
    <li
      ref={ref}
      className={cn(
        {
          'sticky top-[calc(var(--layout-section-header-height)-2px)] bottom-0 z-[15]':
            sticky,
        },
        className,
      )}
    >
      <RoundSessionRowDesktop
        className='h-full md:hidden'
        discipline={discipline}
        isFirstOnPage={isFirstOnPage}
        withContestLink={withContestLink}
        podiumColors={podiumColors}
        place={place}
        session={session}
        onPlaceClick={onPlaceClick}
        revealedAttemptsNumber={revealedAttemptsNumber}
        isPlacePreliminary={isPlacePreliminary}
      />
      <RoundSessionRowTablet
        className='hidden md:block'
        discipline={discipline}
        isFirstOnPage={isFirstOnPage}
        withContestLink={withContestLink}
        podiumColors={podiumColors}
        place={place}
        session={session}
        onPlaceClick={onPlaceClick}
        revealedAttemptsNumber={revealedAttemptsNumber}
        isPlacePreliminary={isPlacePreliminary}
      />
    </li>
  )
}

export function RoundSessionRowSkeleton() {
  return (
    <div className='bg-grey-100 h-16 animate-pulse rounded-xl sm:h-[7.25rem] md:h-[5.25rem]'></div>
  )
}

export function RoundSessionHeader({
  withContestLink = false,
}: {
  withContestLink?: boolean
}) {
  return (
    <div className='text-grey-40 flex pl-2 whitespace-nowrap md:hidden'>
      <span className='mr-2 w-11 text-center'>Place</span>
      <span className='mr-2'>Type</span>
      <span className='flex-1'>Nickname</span>
      <span className='mr-4 w-24 text-center lg:w-20'>Average time</span>
      <span className='mr-2 flex gap-2 lg:gap-1'>
        {Array.from({ length: 5 }, (_, index) => (
          <span key={index} className='w-24 text-center lg:w-20'>
            Attempt {index + 1}
          </span>
        ))}
      </span>
      {withContestLink && <div className='w-[9.25rem] lg:w-28' />}
    </div>
  )
}

function RoundSessionRowTablet({
  session: { solves, user, session, contestSlug },
  withContestLink,
  place,
  discipline,
  podiumColors,
  isFirstOnPage,
  className,
  onPlaceClick,
  revealedAttemptsNumber,
  isPlacePreliminary,
}: RoundSessionRowProps) {
  const { bestId, worstId } = getBestAndWorstIds(solves)

  const revealedAverage =
    revealedAttemptsNumber !== undefined && revealedAttemptsNumber === 5
  const isInProgress =
    solves.length !== 5 || solves.at(-1)!.status === 'pending'
  return (
    <Accordion.Root
      type='single'
      defaultValue={isInProgress && session.isOwn ? 'result' : undefined}
      collapsible
      asChild
    >
      <Accordion.Item value='result' asChild>
        <div className={className}>
          <SpinningBorder
            enabled={session.isOwn && session.isFinished}
            color={tailwindConfig.theme.colors.secondary[60]}
            className={cn('rounded-xl', {
              'border-grey-20 border border-dashed': !session.isFinished,
            })}
          >
            <div
              className={cn(
                'flex min-h-[5.25rem] flex-wrap items-center rounded-xl px-4 py-3 sm:min-h-[7.25rem] sm:p-4',
                session.isOwn ? 'bg-secondary-80' : 'bg-grey-100',
              )}
            >
              <Accordion.Header className='flex w-full flex-1 items-center sm:grid sm:grid-flow-col sm:grid-cols-[min-content_1fr_min-content] sm:grid-rows-[repeat(2,min-content)] sm:gap-x-3 sm:gap-y-1'>
                <PlaceLabel
                  onClick={onPlaceClick}
                  podiumColors={podiumColors}
                  variant={isPlacePreliminary ? 'dashed' : 'default'}
                  className={cn('mr-3 sm:mr-0', {
                    'cursor-pointer': onPlaceClick,
                  })}
                >
                  {place}
                </PlaceLabel>
                <DisciplineIcon
                  className='mr-3 sm:mr-0'
                  discipline={discipline}
                />
                <span className='vertical-alignment-fix flex flex-1 items-center gap-2 sm:col-span-2 sm:w-auto'>
                  <UsernameLink username={user.name} />
                  <UserBadges user={user} />
                  {session.isOwn && (
                    <ClassicSolveViewLink
                      contestSlug={contestSlug}
                      discipline={discipline}
                    >
                      {(href) => (
                        <UnderlineButton
                          asChild
                          size='sm'
                          className='ml-auto hidden sm:block'
                        >
                          <Link href={href}>Classic solve view</Link>
                        </UnderlineButton>
                      )}
                    </ClassicSolveViewLink>
                  )}
                </span>
                <span className='mr-10 sm:mr-0 sm:flex sm:items-center'>
                  <span className='sm:vertical-alignment-fix text-grey-40 block text-center'>
                    Average time
                  </span>
                  <SolveTimeLabel
                    timeMs={session.result?.timeMs ?? undefined}
                    isDnf={session.result?.isDnf}
                    isAverage={session.isFinished && revealedAverage}
                    isPlaceholder={!session.isFinished || !revealedAverage}
                    className={cn({ 'text-grey-60': !revealedAverage })}
                  />
                </span>
                <Accordion.Trigger className='outline-ring group sm:py-2'>
                  <PlusIcon className='block group-data-[state=open]:hidden' />
                  <MinusIcon className='hidden group-data-[state=open]:block' />
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Content className='data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down w-full overflow-y-clip'>
                <ul className='border-grey-60 grid grid-flow-col grid-cols-[repeat(5,min-content)] grid-rows-[min-content_min-content] justify-end gap-x-2 gap-y-1 border-t pt-4 sm:grid-flow-row sm:grid-cols-2 sm:grid-rows-none sm:items-center sm:gap-y-0 sm:pt-3 sm:pl-2'>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <li key={solves[idx]?.id ?? idx} className='contents'>
                      <span className='text-grey-40 text-center sm:text-left'>
                        Attempt {idx + 1}
                      </span>
                      <span className='relative flex items-center sm:ml-auto sm:text-right'>
                        <SingleAttempt
                          contestSlug={contestSlug}
                          session={session}
                          isFirstOnPage={isFirstOnPage}
                          idx={idx}
                          solves={solves}
                          discipline={discipline}
                          bestId={bestId}
                          worstId={worstId}
                          revealedAttemptsNumber={revealedAttemptsNumber}
                        />
                      </span>
                    </li>
                  ))}
                </ul>
                {withContestLink && (
                  <SecondaryButton
                    asChild
                    size='sm'
                    className='mt-4 h-11 w-full gap-1'
                  >
                    <Link
                      href={`/contests/${contestSlug}/results?discipline=${discipline}&scrollToId=${session.id}`}
                    >
                      <span>Contest {contestSlug}</span>
                      <ArrowRightIcon className='inline-block' />
                    </Link>
                  </SecondaryButton>
                )}
              </Accordion.Content>
            </div>
          </SpinningBorder>
        </div>
      </Accordion.Item>
    </Accordion.Root>
  )
}

function RoundSessionRowDesktop({
  session: { solves, user, session, contestSlug },
  place,
  isPlacePreliminary,
  withContestLink,
  isFirstOnPage,
  discipline,
  podiumColors,
  className,
  onPlaceClick,
  revealedAttemptsNumber,
}: RoundSessionRowProps) {
  const { bestId, worstId } = getBestAndWorstIds(solves)

  const revealedAverage =
    revealedAttemptsNumber !== undefined && revealedAttemptsNumber === 5
  return (
    <div className={className}>
      <SpinningBorder
        color={tailwindConfig.theme.colors.secondary[60]}
        enabled={session.isOwn && session.isFinished}
        className={cn('rounded-xl', {
          'border-grey-40 border border-dashed': !session.isFinished,
        })}
      >
        <div
          className={cn(
            'flex h-full min-h-16 w-full items-center rounded-xl pl-2',
            session.isOwn ? 'bg-secondary-80' : 'bg-grey-100',
          )}
        >
          <PlaceLabel
            onClick={onPlaceClick}
            podiumColors={podiumColors}
            variant={isPlacePreliminary ? 'dashed' : 'default'}
            className={cn('mr-3', { 'cursor-pointer': onPlaceClick })}
          >
            {place}
          </PlaceLabel>
          <DisciplineIcon className='mr-3' discipline={discipline} />
          <span className='vertical-alignment-fix flex min-w-0 flex-1 items-center gap-2 overflow-x-clip text-nowrap text-ellipsis'>
            <UsernameLink username={user.name} />
            <UserBadges user={user} />
          </span>

          <SolveTimeLabel
            timeMs={session.result?.timeMs ?? undefined}
            isDnf={session.result?.isDnf}
            isAverage={session.isFinished && revealedAverage}
            isPlaceholder={!session.isFinished || !revealedAverage}
            className={cn(
              'after:bg-grey-60 relative mr-4 after:absolute after:top-1/2 after:-right-2 after:h-6 after:w-px after:-translate-y-1/2',
              { 'text-grey-60': !session.isFinished || !revealedAverage },
            )}
          />

          <ul className='mr-2 grid grid-cols-[repeat(5,min-content)] gap-x-2 lg:gap-x-1'>
            {Array.from({ length: 5 }).map((_, idx) => {
              return (
                <li key={solves[idx]?.id ?? idx} className='contents'>
                  <span className='sr-only'>Attempt {idx + 1}</span>
                  <span className='relative flex items-center'>
                    <SingleAttempt
                      contestSlug={contestSlug}
                      session={session}
                      isFirstOnPage={isFirstOnPage}
                      idx={idx}
                      discipline={discipline}
                      solves={solves}
                      bestId={bestId}
                      worstId={worstId}
                      revealedAttemptsNumber={revealedAttemptsNumber}
                    />
                  </span>
                </li>
              )
            })}
          </ul>
          {withContestLink && (
            <SecondaryButton
              asChild
              size='lg'
              className='w-[9.25rem] justify-between px-[1.3rem] lg:w-28'
            >
              <Link
                href={`/contests/${contestSlug}/results?discipline=${discipline}&scrollToId=${session.id}`}
              >
                <span>Contest {contestSlug}</span>
                <ArrowRightIcon className='inline-block lg:hidden' />
              </Link>
            </SecondaryButton>
          )}
        </div>
      </SpinningBorder>
    </div>
  )
}

function getBestAndWorstIds(solves: RoundSessionRowProps['session']['solves']) {
  const dnfSolve = solves.find(({ result: { isDnf } }) => isDnf)
  const successful = solves
    .filter(({ result: { timeMs, isDnf } }) => timeMs !== null && !isDnf)
    .sort((a, b) => a.result.timeMs! - b.result.timeMs!)
  const bestId = successful.at(0)?.id
  const worstId = dnfSolve?.id ?? successful.at(-1)?.id

  return { bestId, worstId }
}

function SingleAttempt({
  solves,
  session,
  idx,
  isFirstOnPage,
  worstId,
  bestId,
  discipline,
  contestSlug,
  revealedAttemptsNumber,
}: {
  solves: RoundSession['solves']
  idx: number
  session: RoundSession['session']
  isFirstOnPage: boolean
  contestSlug: string
  discipline: Discipline
  bestId?: number
  worstId?: number
  revealedAttemptsNumber?: number
}) {
  const isInProgress = idx === solves.length
  if (session.isOwn && isInProgress) {
    return (
      <SimulatorProvider>
        <OwnSolveInProgress
          contestSlug={contestSlug}
          discipline={discipline}
          bestId={bestId}
          worstId={worstId}
        />
      </SimulatorProvider>
    )
  }

  const solve = solves[idx]
  if (
    !solve ||
    (!session.isOwn &&
      revealedAttemptsNumber !== undefined &&
      idx >= revealedAttemptsNumber)
  ) {
    return <SolveTimeLabel isPlaceholder className='text-grey-60' />
  }

  return (
    <SolveTimeLinkOrDnf
      canShowHint={isFirstOnPage && idx === 0}
      contestSlug={contestSlug}
      solveId={solve.id}
      discipline={discipline}
      result={solve.result}
      isFestive={solve.isPersonalRecord}
      variant={
        solve.id === bestId
          ? 'best'
          : solve.id === worstId
            ? 'worst'
            : undefined
      }
      extraNumber={getExtraNumber(solve.position)}
      backgroundColorClass={session.isOwn ? 'bg-secondary-80' : 'bg-grey-100'}
    />
  )
}

function OwnSolveInProgress({
  contestSlug,
  discipline,
}: {
  contestSlug: string
  discipline: Discipline
  bestId?: number
  worstId?: number
}) {
  const { state, handleInitSolve } = useSolveForm({ contestSlug, discipline })
  const isClient = useIsClient()

  if (!state || !isClient)
    return (
      <div className='flex w-24 items-center justify-center md:h-14 lg:w-20'>
        <LoadingDots />
      </div>
    )

  return (
    <span className='inline-flex h-full w-24 items-center justify-center md:h-14 lg:w-20'>
      <PrimaryButton
        size='sm'
        autoFocus
        onClick={handleInitSolve}
        className='w-full'
      >
        Solve
      </PrimaryButton>
    </span>
  )
}
